// ============================================================================
// DAS Login Interface — two-column layout (brand panel + form panel)
// ============================================================================
// Visual layout ported from the CRS application (crs-web/src/login-interface.tsx)
// so the Syut platform's login experience is consistent across products.
//
// What's reused from CRS:
//   - `.lg-root` two-column structure (brand sidebar + form panel)
//   - Brand panel: logo, animated node-network SVG, stats strip, feature
//     cards, taglines, footer (responsive collapse for narrow viewports)
//   - Form panel: app identity, username + password fields with icons +
//     show/hide password toggle, error display, Sign In button with spinner
//   - All CSS classes (`lg-*`) and animations
//
// What's DAS-specific:
//   - Content (app name "Digital Approval System", DAS-themed feature cards,
//     approval-workflow taglines)
//   - Auth handlers — kept verbatim from the previous DAS login: useLogin
//     hook → Tenant Hub two-step authenticate → role-gate / super-role
//     override happens inside useAuth.ts. No tenant-select UI is needed
//     here because authService.loginWithTenantHub picks the DAS-enabled
//     tenant + DAS application automatically.
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogin } from './hooks/api/useAuth';
import type { LoginRequest } from './api/types';

interface LoginProps {
  onLoginSuccess?: (user: any) => void;
}

const LoginInterface: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  // ---- State (unchanged from previous DAS login) ----
  const [credentials, setCredentials] = useState<LoginRequest>({
    username: '',
    password: '',
  });
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [usernameError, setUsernameError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  // Refs for focus management
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const { login, loading: isPending } = useLogin();

  // Auto-focus username field on mount
  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  // ---- Validation (unchanged) ----
  const validateUsername = (username: string): boolean => {
    if (!username) {
      setUsernameError('Username is required');
      return false;
    }
    if (username.length < 3) {
      setUsernameError('Username must be at least 3 characters');
      return false;
    }
    setUsernameError('');
    return true;
  };

  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    setPasswordError('');
    return true;
  };

  // ---- Submit / change / key handlers (unchanged behaviour) ----
  const handleSubmit = async () => {
    setError('');

    const isUsernameValid = validateUsername(credentials.username);
    const isPasswordValid = validatePassword(credentials.password);
    if (!isUsernameValid || !isPasswordValid) {
      return;
    }

    try {
      await login(credentials, {
        onSuccess: (user) => {
          navigate('../app');
          onLoginSuccess?.(user);
        },
        onError: (err: any) => {
          const errorMessage =
            err?.response?.data?.message ||
            err?.message ||
            'Invalid username or password';
          setError(errorMessage);
        },
      });
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Login failed. Please check your credentials and try again.';
      setError(errorMessage);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    if (name === 'username') validateUsername(value);
    else if (name === 'password') validatePassword(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (credentials.username.trim() && credentials.password.trim()) {
        handleSubmit();
      } else if (!credentials.username.trim()) {
        usernameRef.current?.focus();
      } else if (!credentials.password.trim()) {
        passwordRef.current?.focus();
      }
    }
  };

  return (
    <div className="lg-root">
      <style>{`
        .lg-root {
          display: flex;
          height: 100vh;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", Roboto, sans-serif;
          --lg-dark: #0f172a;
          --lg-mid: #1e293b;
          --lg-teal: #14b8a6;
          --lg-teal-focus: rgba(20, 184, 166, 0.15);
          --lg-teal-ring: rgba(20, 184, 166, 0.25);
          --lg-slate-50: #f8fafc;
          --lg-slate-100: #f1f5f9;
          --lg-slate-200: #e2e8f0;
          --lg-slate-400: #94a3b8;
          --lg-slate-500: #64748b;
          --lg-slate-700: #334155;
          --lg-slate-900: #0f172a;
          --lg-danger: #ef4444;
          --lg-danger-bg: #fef2f2;
          --lg-danger-border: #fecaca;
        }

        .lg-root * { box-sizing: border-box; }

        /* ── BRAND PANEL ─────────────────────────────────────────────── */
        .lg-brand {
          width: 44%;
          flex-shrink: 0;
          background: linear-gradient(155deg, var(--lg-dark) 0%, var(--lg-mid) 100%);
          display: flex;
          flex-direction: column;
          padding: 2.5rem 3rem;
          position: relative;
          overflow: hidden;
          box-shadow: 8px 0 40px rgba(0, 0, 0, 0.3);
          z-index: 1;
        }

        .lg-brand-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          flex-shrink: 0;
          z-index: 1;
        }

        .lg-brand-logo-img {
          height: 88px;
          width: auto;
          max-width: 440px;
          object-fit: contain;
          display: block;
          /* syut_logo.png is dark teal — invert to pure white so it stands
             out on the dark sidebar gradient. brightness(0) flattens the
             image to solid black, then invert(1) flips black → white. */
          filter: brightness(0) invert(1);
        }

        .lg-graphic {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2.5rem 0;
          z-index: 1;
        }

        .lg-graphic svg {
          width: 100%;
          max-width: 360px;
          height: auto;
        }

        .lg-hub-ring   { animation: lg-ring-pulse 4s ease-in-out infinite; }
        .lg-hub-ring-2 { animation: lg-ring-pulse 4s ease-in-out infinite 1.5s; }
        .lg-hub-ring-3 { animation: lg-ring-pulse 4s ease-in-out infinite 0.8s; }
        .lg-hub-ring-4 { animation: lg-ring-pulse 4s ease-in-out infinite 2.2s; }
        .lg-flow-a { stroke-dasharray: 6 10; animation: lg-flow 2.4s linear infinite; }
        .lg-flow-b { stroke-dasharray: 6 10; animation: lg-flow 3.1s linear infinite 0.9s; }
        .lg-flow-c { stroke-dasharray: 6 10; animation: lg-flow-rev 2.8s linear infinite 0.4s; }

        .lg-taglines { z-index: 1; flex-shrink: 0; }

        .lg-tagline-h {
          font-size: 1.75rem;
          font-weight: 700;
          color: #ffffff;
          line-height: 1.2;
          letter-spacing: -0.035em;
          margin: 0 0 0.625rem;
        }

        .lg-tagline-p {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.38);
          margin: 0;
          font-weight: 400;
          letter-spacing: 0.005em;
        }

        /* ── STATS STRIP ─────────────────────────────────────────────── */
        .lg-stats {
          display: flex;
          align-items: stretch;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 0.75rem;
          margin-bottom: 1.5rem;
          flex-shrink: 0;
          z-index: 1;
          overflow: hidden;
        }

        .lg-stat-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 0.875rem 0.5rem;
          gap: 0.25rem;
        }

        .lg-stat-item + .lg-stat-item {
          border-left: 1px solid rgba(255, 255, 255, 0.08);
        }

        .lg-stat-value {
          font-size: 1.375rem;
          font-weight: 800;
          color: var(--lg-teal);
          letter-spacing: -0.03em;
          line-height: 1;
          /* Keep horizontal-layout stat values on a single line so a long
             label (e.g. "Workflow-Driven") never wraps awkwardly across
             the cell — the vertical-stack breakpoint at 1100px takes over
             for genuinely narrow viewports. */
          white-space: nowrap;
        }

        .lg-stat-label {
          font-size: 0.5625rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.35);
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-align: center;
          /* Labels are allowed to wrap (no nowrap). In the vertical-stack
             layout (≤1440px) the label sits to the right of the value with
             limited room; a long label like "CONFIGURABLE APPROVALS" can
             break onto a second line rather than be clipped. */
        }

        /* ── FEATURE CARDS ───────────────────────────────────────────── */
        .lg-features {
          z-index: 1;
          flex-shrink: 0;
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .lg-feature-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.07);
          border-radius: 0.625rem;
          padding: 0.625rem 0.75rem;
        }

        .lg-feature-icon {
          width: 2rem;
          height: 2rem;
          border-radius: 0.5rem;
          background: rgba(20, 184, 166, 0.12);
          border: 1px solid rgba(20, 184, 166, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: var(--lg-teal);
        }

        .lg-feature-text {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
          min-width: 0;
        }

        .lg-feature-title {
          font-size: 0.8125rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.82);
          letter-spacing: 0.005em;
          line-height: 1.3;
        }

        .lg-feature-desc {
          font-size: 0.6875rem;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.35);
          letter-spacing: 0.005em;
          line-height: 1.4;
        }

        .lg-brand-footer {
          margin-top: 2rem;
          flex-shrink: 0;
          z-index: 1;
        }

        .lg-version {
          font-size: 0.6875rem;
          color: rgba(255, 255, 255, 0.18);
          font-weight: 500;
          letter-spacing: 0.03em;
          display: block;
          margin-bottom: 0.375rem;
        }

        .lg-brand-website {
          font-size: 0.6875rem;
          font-weight: 600;
          color: rgba(20, 184, 166, 0.65);
          text-decoration: none;
          letter-spacing: 0.02em;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          transition: color 0.2s;
        }

        .lg-brand-website:hover { color: rgba(20, 184, 166, 1); }

        /* ── APP IDENTITY ────────────────────────────────────────────── */
        .lg-app-identity {
          margin-bottom: 1.875rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid var(--lg-slate-200);
        }

        .lg-app-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          background: rgba(20, 184, 166, 0.1);
          border: 1px solid rgba(20, 184, 166, 0.22);
          border-radius: 2rem;
          padding: 0.2rem 0.625rem;
          font-size: 0.625rem;
          font-weight: 700;
          color: #0d9488;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .lg-app-badge-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #14b8a6;
          flex-shrink: 0;
        }

        .lg-app-name {
          font-size: 1.3125rem;
          font-weight: 800;
          color: var(--lg-slate-900);
          letter-spacing: -0.03em;
          line-height: 1.2;
          margin: 0 0 0.5rem;
        }

        /* ── FORM PANEL ──────────────────────────────────────────────── */
        .lg-form-panel {
          flex: 1;
          background: #fafbff;
          background-image: radial-gradient(circle, rgba(148, 163, 184, 0.2) 1px, transparent 1px);
          background-size: 22px 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2.5rem 2rem;
          overflow-y: auto;
        }

        .lg-form-inner {
          width: 100%;
          max-width: 400px;
        }

        .lg-form-h1 {
          font-size: 1.625rem;
          font-weight: 700;
          color: var(--lg-slate-900);
          letter-spacing: -0.035em;
          margin: 0 0 0.375rem;
          line-height: 1.2;
        }

        .lg-form-sub {
          font-size: 0.875rem;
          color: var(--lg-slate-500);
          margin: 0 0 2rem;
          font-weight: 400;
          line-height: 1.5;
        }

        /* ── FIELDS ──────────────────────────────────────────────────── */
        .lg-field { margin-bottom: 1.125rem; }

        .lg-label {
          display: block;
          font-size: 0.6875rem;
          font-weight: 700;
          color: var(--lg-slate-700);
          margin-bottom: 0.4rem;
          letter-spacing: 0.07em;
          text-transform: uppercase;
        }

        .lg-label-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.4rem;
        }

        .lg-label-row .lg-label { margin-bottom: 0; }

        .lg-input-wrap { position: relative; }

        .lg-input-icon {
          position: absolute;
          left: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--lg-slate-400);
          display: flex;
          align-items: center;
          pointer-events: none;
          transition: color 0.2s;
        }

        .lg-input {
          width: 100%;
          padding: 0.6875rem 0.875rem 0.6875rem 2.625rem;
          font-size: 0.875rem;
          font-family: inherit;
          font-weight: 500;
          color: var(--lg-slate-900);
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 0.625rem;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          appearance: none;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .lg-input::placeholder { color: var(--lg-slate-400); font-weight: 400; }

        .lg-input:focus {
          border-color: var(--lg-teal);
          box-shadow: 0 0 0 3px var(--lg-teal-focus), 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .lg-input:focus + .lg-input-icon,
        .lg-input-wrap:focus-within .lg-input-icon { color: var(--lg-teal); }

        .lg-input.has-error {
          border-color: var(--lg-danger);
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .lg-input-pw { padding-right: 2.875rem; }

        .lg-pw-toggle {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: var(--lg-slate-400);
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.375rem;
          transition: color 0.2s;
          line-height: 0;
        }

        .lg-pw-toggle:hover { color: var(--lg-slate-700); }

        .lg-field-err {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.75rem;
          color: var(--lg-danger);
          margin-top: 0.375rem;
          font-weight: 500;
        }

        .lg-forgot {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--lg-teal);
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          transition: opacity 0.2s;
          font-family: inherit;
        }

        .lg-forgot:hover { opacity: 0.7; }

        /* ── ERROR ALERT ─────────────────────────────────────────────── */
        .lg-error-alert {
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
          background: var(--lg-danger-bg);
          border: 1.5px solid var(--lg-danger-border);
          border-radius: 0.625rem;
          padding: 0.75rem 1rem;
          margin-top: 1rem;
          margin-bottom: 0.25rem;
          color: #dc2626;
          font-size: 0.8125rem;
          font-weight: 500;
          line-height: 1.45;
        }

        .lg-error-icon { flex-shrink: 0; margin-top: 1px; }

        /* ── SUBMIT BUTTON ───────────────────────────────────────────── */
        .lg-submit {
          width: 100%;
          padding: 0.8125rem 1.25rem;
          font-size: 0.9375rem;
          font-weight: 700;
          font-family: inherit;
          letter-spacing: 0.01em;
          border: none;
          border-radius: 0.625rem;
          background: var(--lg-teal);
          color: var(--lg-dark);
          cursor: pointer;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1.375rem;
          position: relative;
          overflow: hidden;
        }

        .lg-submit::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 60%);
          border-radius: inherit;
          pointer-events: none;
        }

        .lg-submit:hover:not(:disabled) {
          background: #0d9488;
          box-shadow: 0 8px 28px rgba(20, 184, 166, 0.4);
          transform: translateY(-1px);
        }

        .lg-submit:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 4px 12px rgba(20, 184, 166, 0.3);
        }

        .lg-submit:disabled {
          background: var(--lg-slate-200);
          color: var(--lg-slate-400);
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .lg-submit:disabled::after { display: none; }

        .lg-spinner {
          width: 17px;
          height: 17px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: lg-spin 0.75s linear infinite;
          flex-shrink: 0;
        }

        /* ── FORM FOOTER ─────────────────────────────────────────────── */
        .lg-form-footer {
          margin-top: 2rem;
          padding-top: 1.25rem;
          border-top: 1px solid var(--lg-slate-100);
        }

        .lg-footer-links {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-bottom: 0.75rem;
          flex-wrap: wrap;
        }

        .lg-footer-link {
          font-size: 0.75rem;
          color: var(--lg-slate-400);
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }

        .lg-footer-link:hover { color: var(--lg-teal); }

        .lg-footer-link-website { color: var(--lg-teal); font-weight: 600; }
        .lg-footer-link-website:hover { color: #0d9488; }

        .lg-copyright {
          text-align: center;
          font-size: 0.6875rem;
          color: var(--lg-slate-400);
          margin: 0;
          font-weight: 400;
        }

        /* ── ANIMATIONS ──────────────────────────────────────────────── */
        @keyframes lg-spin { to { transform: rotate(360deg); } }

        @keyframes lg-ring-pulse {
          0%, 100% { opacity: 0.12; }
          50%      { opacity: 0.28; }
        }

        @keyframes lg-flow {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: -32; }
        }

        @keyframes lg-flow-rev {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: 32; }
        }

        @keyframes lg-enter {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .lg-form-inner { animation: lg-enter 0.55s cubic-bezier(0.16, 1, 0.3, 1) both; }

        /* ── RESPONSIVE ──────────────────────────────────────────────── */
        /* Stack stats vertically as soon as the window narrows below
           full-screen-laptop size (1440px). Horizontal 3-cell only works
           cleanly at desktop widths because the brand panel is 44% of
           viewport and divides into three ~210px cells — the longest
           value ("Workflow-Driven") needs that much. Below 1440px the
           vertical stack gives each value its full panel width. */
        @media (max-width: 1440px) {
          .lg-stats { flex-direction: column; }
          .lg-stat-item {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 0.625rem 0.875rem;
            gap: 0.5rem;
          }
          .lg-stat-item + .lg-stat-item {
            border-left: none;
            border-top: 1px solid rgba(255, 255, 255, 0.08);
          }
          .lg-stat-value { font-size: 0.9375rem; letter-spacing: -0.015em; text-align: left; white-space: nowrap; }
          .lg-stat-label {
            font-size: 0.5rem;
            letter-spacing: 0.06em;
            text-align: right;
            line-height: 1.25;
            /* No nowrap — long labels (e.g. "CONFIGURABLE APPROVALS") wrap
               onto two lines instead of being clipped by overflow:hidden. */
          }
        }

        @media (max-width: 900px) {
          .lg-brand { width: 40%; padding: 1.75rem 1.5rem; }
          .lg-tagline-h { font-size: 1.5rem; }
          .lg-feature-desc { display: none; }
          .lg-feature-item { padding: 0.5rem 0.625rem; }
          .lg-brand-footer { margin-top: 1rem; }
        }

        @media (max-width: 820px) {
          .lg-root {
            flex-direction: column;
            height: auto;
            min-height: 100vh;
            overflow-x: hidden;
          }

          .lg-brand {
            width: 100%;
            flex-direction: row;
            align-items: center;
            padding: 0.875rem 1.375rem;
            flex-shrink: 0;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
          }

          .lg-graphic,
          .lg-stats,
          .lg-features,
          .lg-taglines,
          .lg-brand-footer { display: none; }

          .lg-form-panel { flex: 1; align-items: flex-start; padding: 2rem 1.5rem 2.5rem; }
          .lg-form-inner { max-width: 100%; }
        }

        @media (max-width: 480px) {
          .lg-brand { padding: 0.625rem 1rem; }
          .lg-brand-logo-img { height: 50px; }
          .lg-form-panel { padding: 1.25rem 1rem 1.75rem; }
          .lg-form-h1 { font-size: 1.375rem; }
          .lg-form-sub { font-size: 0.8125rem; margin-bottom: 1.25rem; }
          .lg-app-name { font-size: 1.125rem; }
          .lg-app-identity { margin-bottom: 1.25rem; padding-bottom: 1rem; }
          .lg-footer-links { gap: 0.5rem; }
          .lg-footer-link { font-size: 0.6875rem; }
        }

        @media (max-width: 360px) {
          .lg-brand-logo-img { height: 42px; }
          .lg-form-h1 { font-size: 1.25rem; }
        }
      `}</style>

      {/* ── LEFT BRAND PANEL ────────────────────────────────────────── */}
      <aside className="lg-brand">
        <div className="lg-brand-logo">
          <img
            src={`${process.env.PUBLIC_URL}/Logos/syut_logo.png`}
            alt="Syut Technologies"
            className="lg-brand-logo-img"
          />
        </div>

        {/* Abstract node-network graphic (decorative) */}
        <div className="lg-graphic">
          <svg viewBox="0 0 460 340" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
              <filter id="lg-glow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Node-to-node lines (muted) */}
            <line x1="40" y1="40" x2="142" y2="55" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="320" y1="38" x2="428" y2="122" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="428" y1="122" x2="444" y2="178" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="444" y1="178" x2="402" y2="232" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="402" y1="232" x2="300" y2="212" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="220" y1="232" x2="234" y2="312" stroke="rgba(148,163,184,0.16)" strokeWidth="1"/>
            <line x1="234" y1="312" x2="356" y2="284" stroke="rgba(148,163,184,0.16)" strokeWidth="1"/>
            <line x1="62" y1="300" x2="234" y2="312" stroke="rgba(148,163,184,0.14)" strokeWidth="1"/>

            {/* Hub-connected lines (teal) */}
            <line x1="82" y1="92" x2="40" y2="40" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="82" y1="92" x2="142" y2="55" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="82" y1="92" x2="94" y2="180" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="142" y1="55" x2="192" y2="114" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="320" y2="38" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="428" y2="122" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="252" y2="162" stroke="rgba(20,184,166,0.18)" strokeWidth="1"/>
            <line x1="192" y1="114" x2="252" y2="162" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="252" y2="162" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="356" y2="284" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="156" y2="264" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="252" y1="162" x2="220" y2="232" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="220" y2="232" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="94" y2="180" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="62" y2="300" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="234" y2="312" stroke="rgba(20,184,166,0.18)" strokeWidth="1"/>

            {/* Animated flow lines */}
            <line x1="82" y1="92" x2="252" y2="162" stroke="rgba(20,184,166,0.55)" strokeWidth="1.5" className="lg-flow-a"/>
            <line x1="252" y1="162" x2="156" y2="264" stroke="rgba(20,184,166,0.55)" strokeWidth="1.5" className="lg-flow-b"/>
            <line x1="378" y1="78" x2="252" y2="162" stroke="rgba(20,184,166,0.45)" strokeWidth="1.5" className="lg-flow-c"/>

            {/* Regular nodes */}
            <circle cx="40"  cy="40"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="142" cy="55"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="192" cy="114" r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="320" cy="38"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="428" cy="122" r="3"   fill="rgba(148,163,184,0.4)"/>
            <circle cx="300" cy="212" r="3.5" fill="rgba(148,163,184,0.45)"/>
            <circle cx="402" cy="232" r="3"   fill="rgba(148,163,184,0.38)"/>
            <circle cx="94"  cy="180" r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="220" cy="232" r="3.5" fill="rgba(148,163,184,0.45)"/>
            <circle cx="62"  cy="300" r="2.5" fill="rgba(148,163,184,0.32)"/>
            <circle cx="234" cy="312" r="2.5" fill="rgba(148,163,184,0.32)"/>
            <circle cx="356" cy="284" r="3"   fill="rgba(148,163,184,0.38)"/>
            <circle cx="444" cy="178" r="2.5" fill="rgba(148,163,184,0.32)"/>

            {/* Hub glow rings (animated pulse) */}
            <circle cx="82"  cy="92"  r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring"/>
            <circle cx="252" cy="162" r="18" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-2"/>
            <circle cx="378" cy="78"  r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-3"/>
            <circle cx="156" cy="264" r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-4"/>

            {/* Hub nodes (teal, glowing) */}
            <circle cx="82"  cy="92"  r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="252" cy="162" r="7.5" fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="378" cy="78"  r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="156" cy="264" r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
          </svg>
        </div>

        {/* Stats strip — DAS-themed */}
        <div className="lg-stats">
          <div className="lg-stat-item">
            <span className="lg-stat-value">Multi-Tenant</span>
            <span className="lg-stat-label">Isolated by Design</span>
          </div>
          <div className="lg-stat-item">
            <span className="lg-stat-value">Audit-Ready</span>
            <span className="lg-stat-label">Every Action Tracked</span>
          </div>
          <div className="lg-stat-item">
            <span className="lg-stat-value">Workflow-Driven</span>
            <span className="lg-stat-label">Configurable Approvals</span>
          </div>
        </div>

        {/* Feature cards — DAS approval-platform highlights */}
        <div className="lg-features">
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Role-based Access Control</span>
              <span className="lg-feature-desc">Fine-grained permissions per user and team</span>
            </div>
          </div>
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Configurable Approval Workflows</span>
              <span className="lg-feature-desc">Multi-level chains, delegation, and escalation rules</span>
            </div>
          </div>
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8v4l3 3"/>
                <circle cx="12" cy="12" r="10"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Complete Audit Trail</span>
              <span className="lg-feature-desc">Every submission, approval, and action timestamped</span>
            </div>
          </div>
        </div>

        <div className="lg-taglines">
          <p className="lg-tagline-h">Faster decisions.<br/>Full accountability.</p>
          <p className="lg-tagline-p">Bringing technology to people.</p>
        </div>

        <div className="lg-brand-footer">
          <span className="lg-version">© {new Date().getFullYear()} Syut Technologies</span>
          <a
            href="https://syuttechnologies.com"
            target="_blank"
            rel="noopener noreferrer"
            className="lg-brand-website"
          >
            syuttechnologies.com
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>
      </aside>

      {/* ── RIGHT FORM PANEL ────────────────────────────────────────── */}
      <main className="lg-form-panel">
        <div className="lg-form-inner">
          {/* App identity */}
          <div className="lg-app-identity">
            <p className="lg-app-name">Digital Approval System</p>
            <div className="lg-app-badge">
              <span className="lg-app-badge-dot" />
              Enterprise Approval &amp; Workflow Platform
            </div>
          </div>

          <h1 className="lg-form-h1">Welcome back</h1>
          <p className="lg-form-sub">Sign in to your account to continue</p>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {/* Username */}
            <div className="lg-field">
              <label htmlFor="lg-username" className="lg-label">User ID</label>
              <div className="lg-input-wrap">
                <span className="lg-input-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
                <input
                  ref={usernameRef}
                  type="text"
                  id="lg-username"
                  name="username"
                  value={credentials.username}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  disabled={isPending}
                  placeholder="Enter your user ID"
                  aria-label="User ID"
                  aria-invalid={!!usernameError}
                  aria-describedby={usernameError ? 'lg-username-err' : undefined}
                  className={`lg-input${usernameError ? ' has-error' : ''}`}
                  autoComplete="username"
                />
              </div>
              {usernameError && (
                <p id="lg-username-err" className="lg-field-err">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10" opacity="0.2"/>
                    <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {usernameError}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="lg-field" style={{ marginBottom: '0.5rem' }}>
              <div className="lg-label-row">
                <label htmlFor="lg-password" className="lg-label">Password</label>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => alert('Please contact your administrator to reset your password.')}
                  className="lg-forgot"
                >
                  Forgot?
                </button>
              </div>
              <div className="lg-input-wrap">
                <span className="lg-input-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  ref={passwordRef}
                  type={showPassword ? 'text' : 'password'}
                  id="lg-password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  disabled={isPending}
                  placeholder="Enter your password"
                  aria-label="Password"
                  aria-invalid={!!passwordError}
                  aria-describedby={passwordError ? 'lg-pw-err' : undefined}
                  className={`lg-input lg-input-pw${passwordError ? ' has-error' : ''}`}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="lg-pw-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              {passwordError && (
                <p id="lg-pw-err" className="lg-field-err">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10" opacity="0.2"/>
                    <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {passwordError}
                </p>
              )}
            </div>

            {/* Server-side error (e.g. role-gate rejection) */}
            {error && (
              <div className="lg-error-alert">
                <svg className="lg-error-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="10" opacity="0.2"/>
                  <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {error}
              </div>
            )}

            {/* Sign In button */}
            <button
              type="submit"
              disabled={isPending || !!usernameError || !!passwordError}
              className="lg-submit"
            >
              {isPending ? (
                <>
                  <div className="lg-spinner" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="lg-form-footer">
            <div className="lg-footer-links">
              <a href="#privacy" className="lg-footer-link">Privacy Policy</a>
              <a href="#terms" className="lg-footer-link">Terms of Service</a>
              <a href="#support" className="lg-footer-link">Support</a>
              <a
                href="https://syuttechnologies.com"
                target="_blank"
                rel="noopener noreferrer"
                className="lg-footer-link lg-footer-link-website"
              >
                syuttechnologies.com
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '3px', verticalAlign: 'middle' }}>
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            </div>
            <p className="lg-copyright">
              © {new Date().getFullYear()} Syut Technologies. All rights reserved.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginInterface;
